<script setup lang="ts">
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import type { LawCitationInfo } from '~/utils/solr/law-utils'
import { useFootnotes } from '~/composables/useFootnotes'
import { splitIbexaBodyText } from '~/utils/ibexa-bodytext'
import { markingNodeType } from '~/utils/markings'
import { handleHighlights } from '~/utils/solr/highlighting-utils'

const props = defineProps<{
  comment: LawAndCommentary
  rootCommentary?: Pick<LawAndCommentary, 'id' | 'lastUpdated'>
  searchResult: { id: string, highlights: string[], ezMainNodeId?: number | null } | null
  searchResults: Array<{ id: string, highlights: string[], ezMainNodeId: number | null }> | null
  law?: LawCitationInfo
}>()

const { $parseStringToDocument } = useNuxtApp()
const site = useSiteConfig()

const commentRef = useTemplateRef<HTMLElement>('commentText')
const { scrollToElement } = useScrollToElement('aside')

const highlightScrollId = computed(() => `highlight-${props.comment.id}`)

const matchingResult = computed(() => {
  return props.searchResults?.find(r => r.ezMainNodeId !== null && props.comment.ezMainNodeId !== null && r.ezMainNodeId === props.comment.ezMainNodeId)
})

const isScrollTarget = computed(() => {
  return props.comment.ezMainNodeId !== null && props.searchResult?.ezMainNodeId === props.comment.ezMainNodeId
})

const formattedBodytext = computed(() => {
  const formattedBodytext = $parseStringToDocument(props.comment?.bodytext, ['clean-corrupted-attributes', 'anchor-target-blank'], {
    siteUrl: site.url,
  }).body.innerHTML

  if (!matchingResult.value) {
    return formattedBodytext
  }

  return handleHighlights(formattedBodytext, matchingResult.value.highlights, isScrollTarget.value ? highlightScrollId.value : '')
})

const parsedBodytext = computed(() =>
  $parseStringToDocument(formattedBodytext.value, []),
)

const chunks = computed(() => {
  return splitIbexaBodyText(parsedBodytext.value)
})

const hasMarkingScrollTarget = useMarkingScrollTarget()

// `props.comment.id` og ikke objektet: en refetch som gir samme dokument skal
// ikke trigge scroll på nytt (60991b37).
watch([() => props.comment.id, () => props.searchResult], () => {
  if (hasMarkingScrollTarget.value) {
    return
  }

  const hasMatchingHighlight = props.searchResult?.id && (props.comment.ezMainNodeId !== null && props.comment?.ezMainNodeId === props.searchResult.ezMainNodeId)

  if (hasMatchingHighlight) {
    nextTick(() => {
      scrollToElement(highlightScrollId.value, true)
    })
  }
  else {
    scrollToElement(props.comment.id)
  }
}, { immediate: true })

useFootnotes(commentRef)
useInternalLinkHandler(commentRef, 'aside')
</script>

<template>
  <div>
    <div
      :id="comment.id"
      ref="commentText"
      :key="comment.id"
      class="flex flex-col gap-l max-w-3xl mx-auto px-m"
      data-citation-source="commentary"
      data-marking-content-type="commentary"
      :data-marking-work-id="comment.editionId ?? undefined"
      :data-marking-document-id="comment.id"
      :data-marking-root-document-id="rootCommentary?.id"
      :data-marking-root-editorial-updated-at="rootCommentary?.lastUpdated ?? undefined"
      :data-marking-unit-id="comment.id"
      :data-marking-unit-title="comment.title"
      :data-marking-node-type="markingNodeType(comment.nodeType)"
      :data-citation-law-id="law?.id ?? comment?.lawId ?? undefined"
      :data-citation-title="law?.title"
      :data-citation-commentary-title="law?.shortTitle"
      :data-citation-paragraph-title="comment?.title"
      :data-citation-chapter-title="comment?.chapterTitle ?? undefined"
      :data-citation-law-fragment-id="comment?.lawFragmentId ?? undefined"
      :data-citation-authors="comment?.authors?.join(', ')"
      :data-citation-author-slugs="comment?.authorSlug?.join(', ') ?? undefined"
      :data-citation-last-updated="comment?.lastUpdated"
      :data-citation-original-publication-date="comment?.originalPublicationDate ?? undefined"
      :data-citation-edition-id="comment?.editionId"
      :data-citation-timestamp="comment?.timestamp ?? undefined"
      :data-citation-law-timestamp="law?.timestamp ?? undefined"
      :data-citation-date-in-force="law?.dateInForce ?? undefined"
      :data-citation-last-change-in-force="law?.lastChangeInForce ?? undefined"
      :data-citation-legal-areas="comment?.legal_areas?.join(', ') ?? undefined"
    >
      <template v-for="(chunk, i) in chunks" :key="i">
        <BodyText v-if="chunk.type === 'html'" source="ibexa" :html="chunk.html" />
        <IbexaFigure v-else v-bind="chunk.props" />
      </template>
    </div>
  </div>
</template>

<style scoped lang="postcss">
:deep(.body-text) {
  @apply flex flex-col;

  a {
    @apply break-all dark:text-white;
  }

  .margtekst {
    @apply hidden;
  }

  h2 {
    @apply font-body text-base sm:text-lg font-semibold dark:text-white;
  }

  h3 {
    @apply text-lg font-bold dark:text-white;
  }

  h4 {
    @apply text-base font-medium italic dark:text-white;
  }

  h5 {
    @apply text-base font-normal italic dark:text-white;
  }

  /* Restore visible focus styles when anchor targets receive programmatic focus, since global CSS removes the default browser outline. */
  [data-anchor-focus]:focus {
    outline: 3px solid theme('colors.blurple.300');
    outline-offset: 2px;
    border-radius: 2px;
  }
}
</style>
